// Leaf imports, not the `../data-table` barrel: the barrel pulls in components that read `window`
// at import time, which keeps these helpers out of the DOM-free test config.
import { FilterMethod } from '../data-table/components/filters/filters.models';
import { parseFilter } from '../data-table/data-table.service';

export const SEARCH_ID_CAP = 100;
const COMMENT_PAGE_SIZE = 100;

/** Where the task-list search box looks. Exactly one at a time — see the search popover. */
export type TaskSearchIn = 'subject' | 'body' | 'coding' | 'rule';

/**
 * A coder rule number as an operator types it: digits, optionally after a `#`. Anything else is
 * `null`, never a guess — `GET /claims` takes the number raw (`filterCodingRuleNumber=12`), and the
 * `EQ:12` form the other filters use answers 500 (measured on sandbox 2026-10-01).
 */
export function parseCoderRuleNumber(input: string): number | null {
    const match = input.trim().match(/^#?\s*(\d{1,9})$/);
    return match ? Number(match[1]) : null;
}

/**
 * Build the FTS:… value for a full-text filter (same path as mail OCR FullText).
 * Uses CT (contains all words) so multi-word queries require each token.
 */
function buildFtsValue(filterId: string, query: string): string {
    const trimmed = query.trim();
    if (!trimmed) {
        return '';
    }
    return parseFilter({
        filterId,
        filterType: 'FullText',
        filterMethod: FilterMethod.CT,
        value: trimmed,
    });
}

/** FTS value for `filterBody` on GET /comment. */
export function buildCommentBodyFtsValue(query: string): string {
    return buildFtsValue('filterBody', query);
}

/** FTS value for `filterResult` on GET /coding. */
export function buildCodingResultFtsValue(query: string): string {
    return buildFtsValue('filterResult', query);
}

export interface ICommentSearchHit {
    messageId?: string;
}

export interface ICodingSearchHit {
    id?: string;
    claimId?: string;
}

export interface ICoderRuleClaimHit {
    id?: string;
}

/**
 * Page through a list endpoint and collect unique IDs (capped).
 *
 * The cap is the ceiling of the whole "search a child resource, then filter tasks by the IDs it
 * returns" approach: those IDs travel in the query string, so it cannot be raised far. It is also
 * applied BEFORE the open/closed tab filter, so a term matching mostly closed tasks spends the cap
 * on them. Both are known limits, not accidents — see the search notes in task-list-fetch.
 */
async function collectUniqueIds<THit>(options: {
    fetchPage: (args: { limit: number; offset: number }) => Promise<THit[]>;
    readId: (hit: THit) => string | undefined;
    cap?: number;
}): Promise<string[]> {
    const cap = options.cap ?? SEARCH_ID_CAP;
    const ids = new Set<string>();
    let offset = 0;

    while (ids.size < cap) {
        const page = await options.fetchPage({
            limit: COMMENT_PAGE_SIZE,
            offset,
        });
        if (!page.length) {
            break;
        }
        for (const hit of page) {
            const id = options.readId(hit);
            if (id) {
                ids.add(id);
                if (ids.size >= cap) {
                    break;
                }
            }
        }
        if (page.length < COMMENT_PAGE_SIZE) {
            break;
        }
        offset += COMMENT_PAGE_SIZE;
    }

    return [...ids];
}

/** {@link collectUniqueIds} over an FTS filter value; an empty value searches nothing. */
async function collectIdsFromFtsSearch<THit>(options: {
    ftsValue: string;
    fetchPage: (args: { fts: string; limit: number; offset: number }) => Promise<THit[]>;
    readId: (hit: THit) => string | undefined;
    cap?: number;
}): Promise<string[]> {
    if (!options.ftsValue) {
        return [];
    }
    return collectUniqueIds({
        fetchPage: ({ limit, offset }) => options.fetchPage({ fts: options.ftsValue, limit, offset }),
        readId: options.readId,
        cap: options.cap,
    });
}

/**
 * Page through comment FTS results and collect unique message IDs (capped).
 */
export async function collectMessageIdsFromCommentSearch(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCommentBodyFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchComments({ filterBody: [fts], limit, offset }),
        readId: hit => hit.messageId,
        cap: options.cap,
    });
}

/**
 * Page through coding FTS results and collect the unique CLAIM IDs they belong to (capped).
 *
 * Claim, not coding: measured 2026-09-15 on a live account, coding-problem tasks carry a `claims`
 * link (50/50 sampled) and never a `codings` link (0/50), so `filterCodingId` on GET /message has
 * nothing to match. Every coding carries `claimId`, and filtering messages by claim is the same
 * path the Patient search already uses.
 */
export async function collectClaimIdsFromCodingSearch(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCodingResultFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchCodings({ filterResult: [fts], limit, offset }),
        readId: hit => hit.claimId,
        cap: options.cap,
    });
}

/**
 * Page through `GET /claims?filterCodingRuleNumber=N` and collect the claim IDs (capped).
 *
 * Built on the reading that the filter returns the claims where rule N FIRED (applied or halted),
 * not every claim it was evaluated on — the request said "ran against", and the user decided on
 * 2026-10-01 to build it as "fired" while the backend confirms (FE-126). Sandbox has no rule data,
 * so it could not settle this. If it turns out to mean "ran", nothing here changes: the server
 * decides the set, and every rule would simply return nearly every coded claim.
 */
export async function collectClaimIdsFromCoderRuleSearch(options: {
    ruleNumber: number;
    searchClaims: (args: {
        filterCodingRuleNumber: number;
        limit: number;
        offset: number;
    }) => Promise<ICoderRuleClaimHit[]>;
    cap?: number;
}): Promise<string[]> {
    return collectUniqueIds({
        fetchPage: ({ limit, offset }) =>
            options.searchClaims({ filterCodingRuleNumber: options.ruleNumber, limit, offset }),
        readId: hit => hit.id,
        cap: options.cap,
    });
}

/**
 * One query-keyed, single-flight cache of search → IDs.
 *
 * Shared by the comment, coding and coder-rule searches on purpose: the supersede logic below
 * (ignore a resolution once a newer query has replaced it) is the part that goes subtly wrong
 * when copied.
 */
function createSearchIdCache(): {
    get: (term: string, run: () => Promise<string[]>) => Promise<string[]>;
    clear: () => void;
} {
    let cacheQuery: string | null = null;
    let cacheIds: string[] | null = null;
    let inflight: Promise<string[]> | null = null;

    const clear = (): void => {
        cacheQuery = null;
        cacheIds = null;
        inflight = null;
    };

    return {
        clear,
        get: (term, run) => {
            if (cacheQuery === term && cacheIds) {
                return Promise.resolve(cacheIds);
            }
            if (cacheQuery === term && inflight) {
                return inflight;
            }

            cacheQuery = term;
            cacheIds = null;
            let pending: Promise<string[]>;
            pending = run().then(
                ids => {
                    // Ignore writes after clear or a newer query superseded this request.
                    if (cacheQuery === term && inflight === pending) {
                        cacheIds = ids;
                        inflight = null;
                    }
                    return ids;
                },
                error => {
                    if (cacheQuery === term && inflight === pending) {
                        inflight = null;
                    }
                    throw error;
                },
            );
            inflight = pending;

            return pending;
        },
    };
}

const commentFtsCache = createSearchIdCache();
const codingFtsCache = createSearchIdCache();
const coderRuleCache = createSearchIdCache();

export interface ICoderRuleSearchSummary {
    ruleNumber: number;
    claimCount: number;
    /** The claim list stopped at the cap, so the tasks shown are not all of the rule's tasks. */
    capped: boolean;
}

let coderRuleSummary: ICoderRuleSearchSummary | null = null;

/**
 * What the last resolved coder-rule search found — read by the search bar to say when the
 * results stop at the cap. Silent truncation would read as "these are all the halts".
 */
export function getCoderRuleSearchSummary(): ICoderRuleSearchSummary | null {
    return coderRuleSummary;
}

/** Drop every cached search → ID mapping. Call when the search input text changes. */
export function clearTaskSearchIdCaches(): void {
    commentFtsCache.clear();
    codingFtsCache.clear();
    coderRuleCache.clear();
    coderRuleSummary = null;
}

/**
 * Return cached comment FTS message IDs for `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCommentFtsMessageIds(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    return commentFtsCache.get(term, () =>
        collectMessageIdsFromCommentSearch({
            query: term,
            searchComments: options.searchComments,
            cap: options.cap,
        }),
    );
}

/**
 * Return cached claim IDs for a coding-result `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCodingFtsClaimIds(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    return codingFtsCache.get(term, () =>
        collectClaimIdsFromCodingSearch({
            query: term,
            searchCodings: options.searchCodings,
            cap: options.cap,
        }),
    );
}

/**
 * Return cached claim IDs for a coder rule number, or page GET /claims once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCoderRuleClaimIds(options: {
    ruleNumber: number;
    searchClaims: (args: {
        filterCodingRuleNumber: number;
        limit: number;
        offset: number;
    }) => Promise<ICoderRuleClaimHit[]>;
    cap?: number;
}): Promise<string[]> {
    const cap = options.cap ?? SEARCH_ID_CAP;
    const { ruleNumber } = options;
    const ids = await coderRuleCache.get(String(ruleNumber), () =>
        collectClaimIdsFromCoderRuleSearch({ ruleNumber, searchClaims: options.searchClaims, cap }),
    );
    coderRuleSummary = { ruleNumber, claimCount: ids.length, capped: ids.length >= cap };
    return ids;
}
